import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'

interface MergeGhostProfilesData {
  keptGhostProfileId: string
  duplicateGhostProfileId: string
}

interface MergeGhostProfilesResponse {
  success: boolean
  // Id of the private note where the server kept what the two cards said
  // differently; null when nothing differed.
  detailsNoteId?: string | null
}

const mergeGhostProfiles = async (data: MergeGhostProfilesData): Promise<MergeGhostProfilesResponse> => {
  const { data: response } = await api.post<MergeGhostProfilesResponse>('/ft_merge-ghost-profiles', data)
  return response
}

interface MergeGhostProfilesCallbacks {
  onSuccess?: (response: MergeGhostProfilesResponse, variables: MergeGhostProfilesData) => void
  onError?: (error: Error, variables: MergeGhostProfilesData) => void
}

// Merges a card into another card of the same person. Same pattern as
// useFtMergeContacts: the callbacks sit on the mutation itself, so a merge
// that answers behind another one, or after the screen was left, still reports.
export const useFtMergeGhostProfiles = (callbacks: MergeGhostProfilesCallbacks = {}) => {
  const queryClient = useQueryClient()

  return useMutation<MergeGhostProfilesResponse, Error, MergeGhostProfilesData>({
    mutationFn: mergeGhostProfiles,
    onSuccess: (response, variables) => callbacks.onSuccess?.(response, variables),
    onError: (error, variables) => callbacks.onError?.(error, variables),
    onSettled: async (_response, _error, { keptGhostProfileId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ft-merge-suggestions'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['suggested-contacts'] }),
        // A merge moves the duplicate's notes and appointments to the kept
        // card, may create one more note, and fills the kept card.
        queryClient.invalidateQueries({ queryKey: ['notes'] }),
        queryClient.invalidateQueries({ queryKey: ['notes-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] }),
        queryClient.invalidateQueries({ queryKey: ['profile', keptGhostProfileId] }),
      ])
    },
  })
}
